import { type CSSProperties, type ReactNode } from "react";

import {
  TEXT_VARIANT_CLASSES,
  TONE_CLASSES,
  type TextTone,
  type TextVariant,
} from "@/blueprint/tokens/Typography";
import { cn } from "@/lib/utils";

export type TextElement =
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "h5"
  | "h6"
  | "p"
  | "span"
  | "div"
  | "blockquote"
  | "figcaption"
  | "li";

const DEFAULT_ELEMENT: Record<TextVariant, TextElement> = {
  d1: "h1",
  h1: "h1",
  h2: "h2",
  h3: "h3",
  h4: "h4",
  h5: "h5",
  h6: "h6",
  "paragraph-lg": "p",
  "paragraph-md": "p",
  "paragraph-sm": "p",
  "careers-section-title": "h2",
  "careers-job-location": "p",
  "wib-card-name": "h3",
  "highlight-card-title": "h3",
  "highlight-card-title-lg": "h3",
  "highlight-card-title-xl": "h3",
  "wib-card-body": "p",
  "configurator-title": "h2",
  "speech-bubble": "p",
  "mini-subtitle": "h3",
  "turntable-hint": "p",
  "film-caption": "p",
  "command-pill": "span",
  "language-phrase": "p",
  "language-name": "p",
  "lockup-bubble": "p",
  "story-year": "p",
  "story-line": "p",
  "serene-poster": "h2",
  "serene-chapter": "h3",
  "serene-intro": "span",
  "serene-italic": "p",
};

type TextProps = {
  variant: TextVariant;
  tone?: TextTone;
  /** Override the rendered element, e.g. an h1 with h2 visuals for the page title. */
  as?: TextElement;
  className?: string | undefined;
  style?: CSSProperties;
  /** Set on any text not in the page language — scripts need it to shape. */
  lang?: string;
  dir?: "ltr" | "rtl";
  children: ReactNode;
};

const Text = ({
  variant,
  tone = "primary",
  as,
  className,
  style,
  lang,
  dir,
  children,
}: TextProps) => {
  const Element = as ?? DEFAULT_ELEMENT[variant];
  return (
    <Element
      className={cn(
        TEXT_VARIANT_CLASSES[variant],
        TONE_CLASSES[tone],
        className,
      )}
      style={style}
      lang={lang}
      dir={dir}
    >
      {children}
    </Element>
  );
};

export default Text;
